import React, { useState, useMemo } from "react";
import { Plus, Minus, ShoppingBag, X, Trash2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";

/**
 * Cart & ordering UI for a public restaurant menu (Premium tier).
 *
 * Exposes three pieces of UI:
 *   1. `useCart`      — hook returning cart state + mutation helpers
 *   2. `AddToCartButton` — small "+" button rendered next to an item price
 *   3. `CartDrawer`   — fixed bottom sheet showing items + submit button
 *
 * The customer's table is captured from the QR URL (`?table=<id>`). All
 * prices are recomputed server-side, so nothing sent by the client is
 * trusted for money.
 */

const CURRENCY = { EUR: "€", USD: "$", TRY: "₺", AED: "AED" };

/** Client-side subtotal for previewing cart lines. Server is authoritative. */
export function computeLineTotal(line) {
  const extrasSum = (line.extras || []).reduce((s, e) => s + (e.price || 0), 0);
  const unit = (line.base_price || 0) + (line.variant_price_delta || 0) + extrasSum;
  return unit * (line.qty || 1);
}

export function useCart() {
  const [lines, setLines] = useState([]);

  const addLine = (item, opts = {}) => {
    const line = {
      key: `${item.id}::${opts.variant || ""}::${(opts.extras || []).map((e) => e.name).sort().join(",")}::${(opts.note || "").trim()}`,
      menu_item_id: item.id,
      name: item.name,
      base_price: item.price || 0,
      qty: opts.qty || 1,
      variant: opts.variant || "",
      variant_price_delta: opts.variant_price_delta || 0,
      extras: opts.extras || [],
      note: opts.note || "",
    };
    setLines((prev) => {
      // Merge if identical config already in cart
      const idx = prev.findIndex((l) => l.key === line.key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + line.qty };
        return next;
      }
      return [...prev, line];
    });
  };

  const setQty = (key, qty) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, qty: Math.max(1, qty) } : l)));
  const setLineNote = (key, note) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, note } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));
  const clear = () => setLines([]);

  const total = useMemo(() => lines.reduce((s, l) => s + computeLineTotal(l), 0), [lines]);
  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);

  return { lines, count, total, addLine, setQty, setLineNote, removeLine, clear };
}

export function AddToCartButton({ item, onOpenPicker, primary, testId }) {
  const needsPicker =
    (item.variants && item.variants.length > 0) || (item.extras && item.extras.length > 0);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onOpenPicker(item, needsPicker);
      }}
      className="inline-flex items-center justify-center h-9 w-9 rounded-full font-bold shadow-sm hover:shadow-md active:scale-95 transition-all"
      style={{ backgroundColor: primary, color: "#ffffff" }}
      aria-label="Add to cart"
      data-testid={testId}
    >
      <Plus className="h-5 w-5" />
    </button>
  );
}

/** Modal shown when an item has variants or extras. */
export function ItemPickerDialog({ item, symbol, primary, onClose, onConfirm }) {
  const { t } = useLang();
  const [variant, setVariant] = useState(item.variants?.[0]?.name || "");
  const [extras, setExtras] = useState([]);
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);

  const variantDelta = useMemo(() => {
    const v = (item.variants || []).find((v) => v.name === variant);
    return v ? v.price_delta || 0 : 0;
  }, [item.variants, variant]);
  const extrasSum = extras.reduce((s, e) => s + e.price, 0);
  const unit = (item.price || 0) + variantDelta + extrasSum;
  const totalPrev = unit * qty;

  const toggleExtra = (ex) => {
    setExtras((prev) =>
      prev.find((e) => e.name === ex.name) ? prev.filter((e) => e.name !== ex.name) : [...prev, ex],
    );
  };

  const confirm = () => {
    onConfirm({
      qty,
      variant,
      variant_price_delta: variantDelta,
      extras,
      note: note.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4" onClick={onClose} data-testid="item-picker-backdrop">
      <div
        className="w-full sm:max-w-md bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-bold text-lg leading-tight">{item.name}</div>
            {item.description && (
              <div className="text-xs opacity-70 mt-0.5">{item.description}</div>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label={t("cart.close")} data-testid="item-picker-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {item.variants?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase opacity-60 mb-2">{t("cart.variant")}</div>
            <div className="grid grid-cols-3 gap-2">
              {item.variants.map((v) => (
                <button
                  key={v.name}
                  onClick={() => setVariant(v.name)}
                  className={`px-3 py-2 rounded-xl border-2 text-sm font-semibold text-center transition-all ${
                    variant === v.name ? "text-white shadow-md" : "bg-secondary hover:border-current"
                  }`}
                  style={variant === v.name ? { backgroundColor: primary, borderColor: primary } : {}}
                  data-testid={`variant-${v.name}`}
                >
                  <div>{v.name}</div>
                  {v.price_delta ? (
                    <div className="text-[10px] opacity-90">+{v.price_delta.toFixed(2)}{symbol}</div>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        )}

        {item.extras?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase opacity-60 mb-2">{t("cart.extras")}</div>
            <div className="space-y-1.5">
              {item.extras.map((ex) => {
                const chosen = !!extras.find((e) => e.name === ex.name);
                return (
                  <label
                    key={ex.name}
                    className={`flex items-center justify-between gap-2 rounded-xl border-2 p-2.5 cursor-pointer transition-all ${
                      chosen ? "border-current" : "border-transparent bg-secondary"
                    }`}
                    style={chosen ? { borderColor: primary } : {}}
                    data-testid={`extra-${ex.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={chosen}
                        onChange={() => toggleExtra(ex)}
                        className="accent-current"
                        style={{ accentColor: primary }}
                      />
                      <span className="text-sm">{ex.name}</span>
                    </div>
                    <span className="text-sm font-semibold">+{ex.price.toFixed(2)}{symbol}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-xs font-semibold uppercase opacity-60 mb-2 flex items-center gap-1"><StickyNote className="h-3 w-3" /> {t("cart.note")}</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder={t("cart.itemNotePlaceholder")}
            className="w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm"
            data-testid="item-picker-note"
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-3 bg-secondary rounded-full p-1" data-testid="item-picker-qty">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-8 w-8 rounded-full bg-white dark:bg-zinc-900 shadow-sm grid place-items-center">
              <Minus className="h-4 w-4" />
            </button>
            <span className="font-bold text-sm w-6 text-center">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="h-8 w-8 rounded-full bg-white dark:bg-zinc-900 shadow-sm grid place-items-center">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={confirm}
            className="flex-1 h-11 rounded-full font-bold text-white shadow-md active:scale-95 transition-transform"
            style={{ backgroundColor: primary }}
            data-testid="item-picker-add"
          >
            {t("cart.addToCart")} — {totalPrev.toFixed(2)}{symbol}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CartDrawer({ cart, slug, tableId, tableLabel, symbol, primary, currency, orderingEnabled }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (cart.lines.length === 0) return;
    if (!orderingEnabled) {
      toast.error("Ordering not enabled for this restaurant");
      return;
    }
    if (!tableId && !tableLabel) {
      toast.error(t("cart.tableRequired"));
      return;
    }
    setSending(true);
    try {
      await api.post(`/public/menu/${slug}/orders`, {
        table_id: tableId || undefined,
        table_label: tableLabel || undefined,
        note: note.trim(),
        items: cart.lines.map((l) => ({
          menu_item_id: l.menu_item_id,
          qty: l.qty,
          variant: l.variant || "",
          extras: l.extras.map((e) => ({ name: e.name, price: e.price })),
          note: l.note || "",
        })),
      });
      toast.success(t("cart.orderSent"), { description: t("cart.orderSentDetail") });
      cart.clear();
      setNote("");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bag button — only visible when cart has items */}
      {cart.count > 0 && !open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 h-14 pl-4 pr-5 rounded-full font-bold shadow-xl inline-flex items-center gap-2 active:scale-95 transition-transform"
          style={{ backgroundColor: primary, color: "#ffffff" }}
          data-testid="cart-open-btn"
        >
          <div className="relative">
            <ShoppingBag className="h-5 w-5" />
            <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full text-[10px] font-bold bg-white text-zinc-900 grid place-items-center">
              {cart.count}
            </span>
          </div>
          <span>{cart.total.toFixed(2)}{symbol}</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-end bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cart-drawer-backdrop">
          <div
            className="w-full max-w-lg mx-auto bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 rounded-t-3xl shadow-2xl p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-bold text-xl">{t("cart.cart")}</div>
                {(tableLabel || tableId) && (
                  <div className="text-xs opacity-70">{t("cart.tableNumber")} <span className="font-bold" data-testid="cart-table-label">{tableLabel || tableId}</span></div>
                )}
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label={t("cart.close")} data-testid="cart-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {cart.lines.length === 0 ? (
              <div className="py-12 text-center opacity-60">{t("cart.empty")}</div>
            ) : (
              <div className="space-y-2.5" data-testid="cart-items">
                {cart.lines.map((l) => {
                  const subtotal = computeLineTotal(l);
                  return (
                    <div key={l.key} className="rounded-2xl border border-zinc-200 dark:border-zinc-800 p-3 space-y-2 bg-zinc-50 dark:bg-zinc-900">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm">{l.name}{l.variant ? ` · ${l.variant}` : ""}</div>
                          {l.extras.length > 0 && (
                            <div className="text-[11px] opacity-70">{l.extras.map((e) => `+ ${e.name}`).join(" · ")}</div>
                          )}
                        </div>
                        <button onClick={() => cart.removeLine(l.key)} className="text-destructive shrink-0" aria-label={t("cart.remove")} data-testid={`cart-remove-${l.menu_item_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <textarea
                        value={l.note}
                        onChange={(e) => cart.setLineNote(l.key, e.target.value)}
                        rows={1}
                        maxLength={200}
                        placeholder={t("cart.itemNotePlaceholder")}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1 text-xs"
                        data-testid={`cart-item-note-${l.menu_item_id}`}
                      />

                      <div className="flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 bg-secondary rounded-full p-1">
                          <button onClick={() => cart.setQty(l.key, l.qty - 1)} className="h-7 w-7 rounded-full bg-white dark:bg-zinc-900 shadow-sm grid place-items-center" data-testid={`cart-dec-${l.menu_item_id}`}>
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="font-bold text-xs w-5 text-center">{l.qty}</span>
                          <button onClick={() => cart.setQty(l.key, l.qty + 1)} className="h-7 w-7 rounded-full bg-white dark:bg-zinc-900 shadow-sm grid place-items-center" data-testid={`cart-inc-${l.menu_item_id}`}>
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="font-bold text-sm" style={{ color: primary }}>{subtotal.toFixed(2)}{symbol}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {cart.lines.length > 0 && (
              <>
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase opacity-60 mb-1">{t("cart.orderNote")}</div>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    maxLength={400}
                    placeholder={t("cart.orderNotePlaceholder")}
                    className="w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm"
                    data-testid="cart-order-note"
                  />
                </div>

                <div className="mt-4 flex items-center justify-between border-t pt-3">
                  <div className="font-bold text-lg">{t("cart.total")}</div>
                  <div className="font-bold text-2xl" style={{ color: primary }} data-testid="cart-total">
                    {cart.total.toFixed(2)}{symbol}
                  </div>
                </div>

                <button
                  onClick={submit}
                  disabled={sending}
                  className="mt-4 w-full h-12 rounded-full font-bold text-white shadow-lg active:scale-95 transition-transform disabled:opacity-50"
                  style={{ backgroundColor: primary }}
                  data-testid="cart-place-order"
                >
                  {sending ? t("cart.sending") : t("cart.placeOrder")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
